/**
 * Page « Tester une raquette » (sections essai-page-hero, essai-page-carte, essai-page-formulaire,
 * essai-page-club) — Padelspirit, 19.10.2026.
 *
 * Un seul choix de raquette pour toute la page :
 *  - les champs de recherche (snippet essai-combo, [data-pdl-essai-combo]) filtrent le JSON des
 *    raquettes (snippet essai-raquettes-json) par les premières lettres, accents ignorés, mots dans
 *    n'importe quel ordre ; flèches + Entrée + Échap ; même logique que sections/essai-demande ;
 *  - l'encart de résultat du haut de page dit si la raquette se teste déjà (N clubs) et propose
 *    « Voir les clubs sur la carte » (descend sur la carte en la filtrant) ou « Demander un test »
 *    (descend sur le formulaire présélectionné) ;
 *  - la carte (animée par assets/club-map.js) : rien n'est ouvert à l'arrivée ; le clic d'un point ou
 *    d'une ligne ouvre le détail du club (adresse, liens, raquettes testables) à la place de la liste,
 *    « Tous les clubs » ou la croix de la fiche flottante y reviennent ; filtre par raquette (points et
 *    lignes des autres clubs estompés) sans rien ouvrir ;
 *  - le formulaire privé est présélectionné (champ caché contact[Modèle]) et affiche le message
 *    adapté ; le choix est partagé dans les deux sens (choisir dans le formulaire remplit le haut).
 *
 * Mesure (dataLayer + Shopify.analytics.publish, voir mesurer()) :
 *  essai_recherche (première saisie d'au moins 2 lettres), essai_recherche_testable (raquette
 *  choisie ayant des clubs), essai_demande_envoyee (formulaire privé validé), essai_club_envoye
 *  (candidature de club validée).
 *
 * Défilement programmé par scrollTo (jamais scrollBy, voir la note defilement-programme-ios).
 */
const dataEl = document.querySelector('[data-pdl-essai-data]');
let raquettes = [];
try {
  raquettes = dataEl ? JSON.parse(dataEl.textContent) : [];
} catch {
  raquettes = [];
}
const parHandle = new Map(raquettes.map((r) => [r.h, r]));
const MAX = 10;
const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---- mesure ---- */
/* ---- source du clic qui a amené ici (?source=fiche, comparateur, accueil, carte-fiche ; sinon « direct ») ----
   Jointe à chaque événement et postée avec les deux formulaires (champ caché contact[Source]) : on sait
   d'où vient chaque demande, même avec très peu de demandes. */
const source = (new URLSearchParams(location.search).get('source') || 'direct').replace(/[^a-z0-9_-]/gi, '').slice(0, 30) || 'direct';
document.querySelectorAll('[data-pdl-tp-source]').forEach((input) => {
  input.value = source;
});

function mesurer(nom, donnees) {
  const detail = Object.assign({ event: nom, page: 'tester-une-raquette', source }, donnees || {});
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(detail);
  } catch {
    /* rien */
  }
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(`padelspirit:${nom}`, detail);
    }
  } catch {
    /* rien */
  }
}

/* ---- texte ---- */
// le titre commence déjà par la marque (« Babolat Viper… ») : on ne la répète pas
function modele(r) {
  if (r.t.toLowerCase().indexOf(r.b.toLowerCase() + ' ') === 0) return r.t.slice(r.b.length + 1);
  return r.t;
}
function libelle(r) {
  return r.b + ' ' + modele(r);
}
// « Viper » et « Vipér » se cherchent pareil : minuscules sans accents
function normaliser(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}
function candidats(q) {
  const mots = normaliser(q).trim() ? normaliser(q).trim().split(/\s+/) : [];
  return raquettes.filter((r) => {
    const corpus = normaliser(r.b + ' ' + r.t);
    return mots.every((m) => corpus.indexOf(m) !== -1);
  });
}

/* ---- choix partagé ---- */
let choisie = null;
const ecouteurs = [];
function surChoix(fn) {
  ecouteurs.push(fn);
}
function choisir(r, origine) {
  choisie = r || null;
  for (const fn of ecouteurs) fn(choisie, origine);
  if (choisie && choisie.c > 0) {
    mesurer('essai_recherche_testable', { raquette: choisie.h, marque: choisie.b, clubs: choisie.c, origine: origine || '' });
  }
}

/* ---- défilement vers une section (scrollTo, en tenant compte de l'en-tête collant) ---- */
function aller(cible) {
  if (!cible) return;
  // --header-height est posée sur <body> par layout/theme.liquid (en-tête collant)
  const entete = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
  const top = cible.getBoundingClientRect().top + window.scrollY - entete - 16;
  window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: reduit ? 'auto' : 'smooth' });
}

/* ---- champs de recherche ---- */
function initCombo(combo) {
  const search = combo.querySelector('[data-pdl-essai-search]');
  const hidden = combo.querySelector('[data-pdl-essai-model]');
  const liste = combo.querySelector('[data-pdl-essai-liste]');
  const aucun = combo.querySelector('[data-pdl-essai-aucun]');
  const clear = combo.querySelector('[data-pdl-essai-clear]');
  if (!search || !liste) return;
  let actif = -1;
  let cherche = false;

  const fermer = () => {
    liste.hidden = true;
    search.setAttribute('aria-expanded', 'false');
    search.removeAttribute('aria-activedescendant');
    actif = -1;
  };

  const afficher = () => {
    const res = candidats(search.value);
    liste.innerHTML = '';
    if (aucun) aucun.hidden = res.length > 0;
    if (!res.length) {
      fermer();
      return;
    }
    res.slice(0, MAX).forEach((r, i) => {
      const li = document.createElement('li');
      li.className = 'pdl-tpc__item';
      li.setAttribute('role', 'option');
      li.id = `${liste.id}-${i}`;
      li.dataset.handle = r.h;
      // vignette du modèle (comme la recherche du comparateur) ; marque en gras devant le modèle
      const img = document.createElement('img');
      img.className = 'pdl-tpc__item-img';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 44;
      img.height = 44;
      if (r.i) img.src = r.i;
      const titre = document.createElement('span');
      titre.className = 'pdl-tpc__item-titre';
      const b = document.createElement('b');
      b.textContent = r.b + ' ';
      titre.append(b, document.createTextNode(modele(r)));
      const clubs = document.createElement('span');
      clubs.className = 'pdl-tpc__item-clubs' + (r.c > 0 ? ' is-ok' : '');
      clubs.textContent = r.c > 0 ? `${r.c} ✓` : '';
      li.append(img, titre, clubs);
      li.addEventListener('mousedown', (e) => {
        e.preventDefault();
        choisir(r, 'recherche');
      });
      liste.appendChild(li);
    });
    liste.hidden = false;
    search.setAttribute('aria-expanded', 'true');
    actif = -1;
  };

  const surligner = (i) => {
    const items = liste.querySelectorAll('[role="option"]');
    if (!items.length) return;
    actif = (i + items.length) % items.length;
    items.forEach((el, k) => el.classList.toggle('is-actif', k === actif));
    search.setAttribute('aria-activedescendant', items[actif].id);
    items[actif].scrollIntoView({ block: 'nearest' });
  };

  // reflète le choix partagé (fait ici ou ailleurs sur la page)
  surChoix((r, origine) => {
    // le choix s'efface quand on retape dans CE champ : sa saisie reste, les autres champs se vident
    const enCours = !r && origine === 'saisie' && document.activeElement === search;
    if (!enCours) search.value = r ? libelle(r) : '';
    if (hidden) hidden.value = r ? `${libelle(r)} (${r.h})` : '';
    if (clear) clear.hidden = !r;
    search.setCustomValidity('');
    fermer();
  });

  search.addEventListener('input', () => {
    if (choisie) choisir(null, 'saisie');
    if (clear) clear.hidden = !search.value;
    if (!cherche && normaliser(search.value).trim().length >= 2) {
      cherche = true;
      mesurer('essai_recherche', { saisie: normaliser(search.value).trim().slice(0, 40) });
    }
    afficher();
  });
  search.addEventListener('focus', () => {
    if (!choisie) afficher();
  });
  search.addEventListener('blur', () => setTimeout(fermer, 120));
  search.addEventListener('keydown', (e) => {
    if (liste.hidden && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) afficher();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      surligner(actif + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      surligner(actif - 1);
    } else if (e.key === 'Enter') {
      const items = liste.querySelectorAll('[role="option"]');
      if (!liste.hidden && items.length) {
        e.preventDefault();
        const cible = items[actif >= 0 ? actif : 0];
        const r = parHandle.get(cible.dataset.handle);
        if (r) choisir(r, 'recherche');
      } else if (!choisie) {
        e.preventDefault();
      }
    } else if (e.key === 'Escape') {
      fermer();
    }
  });
  if (clear) {
    clear.addEventListener('click', () => {
      choisir(null, 'effacer');
      search.value = '';
      clear.hidden = true;
      search.focus();
    });
  }

  // formulaire : un modèle choisi dans la liste est obligatoire
  const form = search.closest('form');
  if (form && hidden) {
    form.addEventListener('submit', (e) => {
      if (choisie) return;
      e.preventDefault();
      search.setCustomValidity(search.dataset.error || '');
      search.reportValidity();
      search.addEventListener('input', () => search.setCustomValidity(''), { once: true });
    });
  }
}
document.querySelectorAll('[data-pdl-essai-combo]').forEach(initCombo);

/* ---- haut de page : encart de résultat, cartes en vedette ---- */
const result = document.querySelector('[data-pdl-tp-result]');
if (result) {
  const brand = result.querySelector('[data-pdl-tp-result-brand]');
  const title = result.querySelector('[data-pdl-tp-result-title]');
  const text = result.querySelector('[data-pdl-tp-result-text]');
  const seeMap = result.querySelector('[data-pdl-tp-see-map]');
  const ask = result.querySelector('[data-pdl-tp-ask]');
  const url = result.querySelector('[data-pdl-tp-result-url]');
  const carte = document.querySelector('[data-pdl-tp-carte]');
  const formulaire = document.getElementById('pdl-essai-formulaire');
  surChoix((r) => {
    if (!r) {
      result.hidden = true;
      return;
    }
    if (brand) brand.textContent = r.b;
    if (title) title.textContent = modele(r);
    const ok = r.c > 0 && carte;
    result.classList.toggle('is-ok', ok);
    if (text) text.textContent = ok ? result.dataset.textOk.replace('[n]', String(r.c)) : result.dataset.textNone;
    if (seeMap) seeMap.hidden = !ok;
    if (ask) ask.hidden = ok;
    if (url) url.href = r.u;
    result.hidden = false;
  });
  if (seeMap) {
    seeMap.addEventListener('click', (e) => {
      e.preventDefault();
      aller(carte);
    });
  }
  if (ask) {
    ask.addEventListener('click', (e) => {
      e.preventDefault();
      aller(formulaire);
      const champ = formulaire && formulaire.querySelector('[data-pdl-essai-form] input:not([type="hidden"]):not([data-pdl-essai-search])');
      if (champ) setTimeout(() => champ.focus({ preventScroll: true }), reduit ? 0 : 500);
    });
  }
}
document.querySelectorAll('[data-pdl-essai-pick]').forEach((btn) => {
  btn.addEventListener('click', () => {
    const r = parHandle.get(btn.dataset.pdlEssaiPick);
    if (!r) return;
    choisir(r, 'vedette');
    const carte = document.querySelector('[data-pdl-tp-carte]');
    aller(r.c > 0 && carte ? carte : document.getElementById('pdl-essai-formulaire'));
  });
});

/* ---- carte de tous les clubs : détail au clic, filtre par raquette ---- */
const carte = document.querySelector('[data-pdl-tp-carte]');
if (carte) {
  const pins = Array.from(carte.querySelectorAll('[data-pdl-club-pin]'));
  const items = Array.from(carte.querySelectorAll('[data-pdl-club]'));
  const side = carte.querySelector('[data-pdl-tp-side]');
  const detail = carte.querySelector('[data-pdl-tp-detail]');
  const fiches = Array.from(carte.querySelectorAll('[data-pdl-tp-detail-for]'));
  const popClose = carte.querySelector('[data-pdl-pop-close]');
  const filtre = carte.querySelector('[data-pdl-tp-filtre]');
  const filtreTitle = carte.querySelector('[data-pdl-tp-filtre-title]');
  const filtreReset = carte.querySelector('[data-pdl-tp-filtre-reset]');
  const etroit = () => window.matchMedia('(max-width: 989px)').matches;

  // détail d'un club à la place de la liste (canton copié depuis la ligne, posé par club-map.js)
  const ouvrirDetail = (index) => {
    if (!detail || !side) return;
    for (const f of fiches) f.hidden = f.dataset.pdlTpDetailFor !== index;
    const item = items.find((i) => i.dataset.pdlClub === index);
    const fiche = fiches.find((f) => f.dataset.pdlTpDetailFor === index);
    const src = item && item.querySelector('[data-pdl-canton]');
    const dst = fiche && fiche.querySelector('[data-pdl-canton-detail]');
    if (src && dst) {
      dst.innerHTML = src.innerHTML;
      dst.hidden = src.hidden;
    }
    detail.hidden = false;
    side.classList.add('is-detail');
    // sur un écran étroit la colonne est sous la carte : on l'amène à l'écran
    if (etroit()) {
      const r = detail.getBoundingClientRect();
      if (r.bottom > window.innerHeight || r.top < 0) aller(detail);
    }
  };
  const fermerDetail = () => {
    if (!detail || !side) return;
    detail.hidden = true;
    side.classList.remove('is-detail');
  };
  // club-map.js ferme la fiche et retire les mises en avant par sa propre croix : on l'appelle
  const toutDeselectionner = () => {
    if (popClose) popClose.click();
    fermerDetail();
  };

  for (const pin of pins) pin.addEventListener('click', () => ouvrirDetail(pin.dataset.pdlClubPin));
  for (const btn of carte.querySelectorAll('[data-pdl-club-btn]')) {
    btn.addEventListener('click', () => ouvrirDetail(btn.dataset.pdlClubBtn));
  }
  if (popClose) popClose.addEventListener('click', fermerDetail);
  const back = carte.querySelector('[data-pdl-tp-detail-back]');
  if (back) back.addEventListener('click', toutDeselectionner);

  // club-map.js met le premier club en avant à l'arrivée : ici rien n'est ouvert tant qu'on n'a pas cliqué
  const reposer = () => {
    if (pins.some((p) => p.classList.contains('is-active'))) toutDeselectionner();
    else if (pins.length) setTimeout(reposer, 150);
  };
  reposer();

  // filtre par raquette choisie : les clubs qui ne la proposent pas s'estompent, rien ne s'ouvre
  const appliquerFiltre = (r) => {
    const actifs = new Set(r && r.c > 0 ? r.k : []);
    const filtrer = actifs.size > 0;
    for (const pin of pins) pin.classList.toggle('is-dim', filtrer && !actifs.has(pin.dataset.club));
    for (const item of items) item.classList.toggle('is-dim', filtrer && !actifs.has(item.dataset.club));
    for (const chip of carte.querySelectorAll('.pdl-carte__chip')) {
      chip.classList.toggle('is-match', filtrer && !!r && chip.getAttribute('href') === r.u);
    }
    if (filtre) {
      filtre.hidden = !filtrer;
      if (filtreTitle && r) filtreTitle.textContent = libelle(r);
    }
  };
  surChoix(appliquerFiltre);
  if (filtreReset) filtreReset.addEventListener('click', () => choisir(null, 'filtre'));
}

/* ---- formulaire privé : message selon la raquette ---- */
const notice = document.querySelector('[data-pdl-essai-notice]');
if (notice) {
  const txt = notice.querySelector('[data-pdl-essai-notice-text]');
  const carteEl = document.querySelector('[data-pdl-tp-carte]');
  surChoix((r) => {
    if (!r || !txt) {
      notice.hidden = true;
      return;
    }
    notice.hidden = false;
    txt.textContent = '';
    if (r.c > 0 && carteEl) {
      notice.classList.add('is-ok');
      txt.append(notice.dataset.textOk.replace('[n]', String(r.c)) + ' ');
      const a = document.createElement('a');
      a.href = '#pdl-essai-carte';
      a.textContent = notice.dataset.textSee;
      a.addEventListener('click', (e) => {
        e.preventDefault();
        aller(carteEl);
      });
      txt.appendChild(a);
    } else {
      notice.classList.remove('is-ok');
      txt.textContent = notice.dataset.textNone;
    }
  });
}

/* ---- envois : mesure juste avant l'envoi natif ---- */
document.querySelectorAll('[data-pdl-tp-form]').forEach((form) => {
  form.addEventListener('submit', (e) => {
    if (e.defaultPrevented || !form.checkValidity()) return;
    if (form.dataset.pdlTpForm === 'club') {
      const marques = Array.from(form.querySelectorAll('input[type="checkbox"]:checked')).map((c) => c.value);
      mesurer('essai_club_envoye', { marques: marques.join(',') });
    } else {
      mesurer('essai_demande_envoyee', { raquette: choisie ? choisie.h : '', clubs: choisie ? choisie.c : 0 });
    }
  });
});

/* ---- ?modele=<handle> : raquette présélectionnée (lien depuis une fiche produit) ---- */
const voulu = new URLSearchParams(location.search).get('modele');
if (voulu && parHandle.has(voulu)) choisir(parHandle.get(voulu), 'lien');
